import { useState } from "react";
import { toast } from "react-toastify";
import { confirmPayment, fetchOrderDetails } from "../services/useService";
import { Order, PaymentHistory } from "src/shared/services/interfacev2";
import { formatTime, formatDate } from "src/business-types/saloon/modules/orders/saloon-order-display/utils/orderUtils";

export const usePayment = (order: Order | null, setOrder: (order: Order) => void, setStatus: (status: string) => void, orderId?: string) => {
  const [paymentMethod, setPaymentMethod] = useState("");
  const [receivedPrice, setReceivedPrice] = useState(0);
  const [modals, setModals] = useState({ cash: false, credit: false, print: false });
  const [error, setError] = useState<string | null>(null);

  const calculateTotalReceived = (orderData: Order | null): number => {
    if (!orderData?.paymentHistory?.length) return 0;

    return orderData.paymentHistory.reduce((sum, item) => sum + Number(item.receivedAmount || 0), 0);
  };

  const handleConfirmPayment = async (method: string) => {
    if (!order || !orderId) {
      toast.error("Order not found");
      return;
    }

    const orderRow = order.orders?.[0];
    if (!orderRow) return;

    const totalAmount = Number(orderRow.finalAmount || orderRow.orderAmount || 0);
    const previousReceived = calculateTotalReceived(order);
    const newTotalReceived = previousReceived + receivedPrice;
    const remainingAmount = Math.max(0, totalAmount - newTotalReceived);
    const outstandingBalance = remainingAmount;

    const newPayment: PaymentHistory = {
      _id: { $oid: "" },
      paidOn: { $date: new Date().toISOString() },
      paymentMethod: method,
      paymentSource: method,
      receivedAmount: receivedPrice,
      refId: Date.now(),
    };

    const updatedPaymentHistory: PaymentHistory[] = [...(order.paymentHistory || []), newPayment];

    const payload = {
      customerId: orderRow.customerId,
      remainingAmount: remainingAmount.toFixed(2),
      finalAmount: orderRow.finalAmount || orderRow.orderAmount || 0,
      paymentHistory: updatedPaymentHistory.map((payment) => ({
        paymentMethod: payment.paymentMethod,
        receivedAmount: payment.receivedAmount,
        paidOn: payment.paidOn,
        paymentSource: payment.paymentSource,
        refId: payment.refId,
      })),
      payments: {
        paymentMethod: method,
        receivedAmount: receivedPrice,
      },
      outstandingBalance: outstandingBalance,
      newOrderAmount: "0",
      date: formatDate(new Date()),
      time: formatTime(new Date()),
      // deliveryDate: formatDate(new Date()),
      // deliveryTime: formatTime(new Date()),
      orderStatus: remainingAmount <= 0 ? "Delivered" : orderRow.status,
      orderDate: formatDate(new Date()),
      orderTime: formatTime(new Date()),
      orders: [
        {
          ...orderRow,
          receivedAmount: newTotalReceived,
          remainingAmount: remainingAmount,
          status: remainingAmount <= 0 ? "Delivered" : orderRow.status,
          paymentSource: method,
        },
      ],
    };

    try {
      await confirmPayment(orderId, payload);
      const res = await fetchOrderDetails(orderId);
      const updatedOrder = res.data.result;
      setOrder(updatedOrder);

      if (remainingAmount <= 0) {
        setStatus("Delivered");
      }

      toast.success("Payment successful", { autoClose: 1500 });

      setModals({ cash: false, credit: false, print: true });

      setPaymentMethod("");
      setReceivedPrice(0);
    } catch (err) {
      console.error(err);
      setError("Payment failed");
      toast.error("Payment failed");
      throw err;
    }
  };

  const handlePaymentMethodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPaymentMethod(value);

    if (value === "Cash") {
      setModals((p) => ({ ...p, cash: true }));
    }

    if (value === "Card") {
      const remaining = Number(order?.orders?.[0]?.remainingAmount || 0);
      setReceivedPrice(remaining);
      setModals((p) => ({ ...p, credit: true }));
    }
  };

  const handleCloseModal = () => {
    setModals({ cash: false, credit: false, print: false });
    setPaymentMethod("");
    setReceivedPrice(0);
  };

  return {
    paymentMethod,
    receivedPrice,
    setReceivedPrice,
    handleConfirmPayment,
    handlePaymentMethodChange,
    handleCloseModal,
    calculateTotalReceived,
    error,
    openCashModal: modals.cash,
    openCreditModal: modals.credit,
    showPrintContent: modals.print,
  };
};
